import { useTranslation } from 'react-i18next';
import { EditableSection } from './EditableSection.js';
import { ProjectNotesEditor } from './ProjectNotesEditor.js';
import { WikiMarkdown } from '../knowledge-wiki/WikiMarkdown.js';
import type { SpecProjectNotes } from '../../../lib/api/spec-requirements.js';

export interface ProjectNotesSectionProps {
  /** 今保存されている補足欄。無い案件では null */
  projectNotes: SpecProjectNotes | null;
  /** この節を開いているか */
  editing: boolean;
  /** 直前の保存で残った注意（上限に近づいた） */
  warnings: string[];
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因 */
  error: unknown;
  /** 編集を開く */
  onEdit: () => void;
  /** 保存する */
  onSave: (body: string) => void;
  /** 編集をやめる */
  onCancel: () => void;
}

/**
 * 補足欄の節。読むときは markdown の本文、編集するときはテキストエリア。
 *
 * **読むときは表にしない。** 6 つの成果物は列が決まった表なので表で出すが、ここは markdown の
 * 自由欄なので、見出し・箇条書き・コードブロックとして本文のまま読める形で出す（書いた人が
 * 分けた構造をそのまま見せる）。描画は wiki 本文と同じ `WikiMarkdown` を使い、記法の扱いを
 * 画面ごとに書き分けない。
 *
 * **レビュー操作は置かない。** 補足欄はレビュー対象ではないので、ほかの節と違い `actions` を
 * 受け取らない（受け取れる形にすると、呼び出し側がレビュー操作を渡せてしまう）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の補足欄（決まった表に入らない情報の markdown 自由欄）を本文として読み、編集して保存する節
 * @graph-connects ProjectNotesEditor [calls] 補足欄の編集
 * @graph-connects WikiMarkdown [calls] 補足欄本文の描画
 */
export function ProjectNotesSection({
  projectNotes,
  editing,
  warnings,
  saving,
  error,
  onEdit,
  onSave,
  onCancel,
}: ProjectNotesSectionProps) {
  const { t } = useTranslation();

  return (
    <EditableSection
      heading={t('spec.projectNotes.heading')}
      action={projectNotes === null ? t('spec.projectNotes.write') : t('spec.projectNotes.edit')}
      editing={editing}
      warnings={warnings}
      onEdit={onEdit}
    >
      {editing ? (
        <ProjectNotesEditor
          projectNotes={projectNotes}
          saving={saving}
          error={error}
          warnings={warnings}
          onSave={onSave}
          onCancel={onCancel}
        />
      ) : projectNotes === null ? (
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">
          {t('spec.projectNotes.empty')}
        </p>
      ) : (
        <>
          {/* 訳が無い・古いときは原文が返る。読んでいるものが原文だと分かるように断る */}
          {projectNotes?.untranslated === true ? (
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('spec.projectNotes.untranslated')}
            </p>
          ) : null}{' '}
          <div className="mt-3">
            <WikiMarkdown markdown={projectNotes.body} />
          </div>
        </>
      )}
    </EditableSection>
  );
}
